import { marketDate, validateDateRange } from "./upcoming-earnings-data-source.js";

const PAGE_SIZE = 100;
const MAX_PAGES = 200;
const GUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i;

// These are announced effective dates in the past window, not a claim of execution.
export class RecentSplitsDataSource {
  constructor({ endpoint = "/api/v1/recent-splits", fetchImpl = globalThis.fetch, now = () => new Date() } = {}) {
    this.endpoint = endpoint; this.fetchImpl = fetchImpl.bind(globalThis); this.now = now;
  }

  async load({ from, to, signal, onProgress = () => {} }) {
    validateDateRange(from, to);
    if (to > marketDate(this.now())) throw new Error("Recent splits must use effective dates through today.");
    const items = []; const seen = new Set(); const seenDates = new Set();
    let offset = 0; let metadata = null; let fingerprint = null;
    for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
      signal?.throwIfAborted();
      const url = new URL(this.endpoint, globalThis.location?.href || "http://localhost/");
      url.searchParams.set("from", from); url.searchParams.set("to", to);
      url.searchParams.set("offset", String(offset)); url.searchParams.set("limit", String(PAGE_SIZE));
      const response = await this.fetchImpl(url.toString(), { signal, credentials: "same-origin", headers: { Accept: "application/json" } });
      signal?.throwIfAborted();
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403
        ? "Sign in to view recent splits." : "Recent splits could not be loaded. Please try again.");
      let page;
      try { page = await response.json(); } catch { throw incomplete(); }
      signal?.throwIfAborted();
      if (!page || page.source !== "AnnouncedEffectiveDates" || page.from !== from || page.to !== to || page.offset !== offset
        || page.limit !== PAGE_SIZE || !Array.isArray(page.items) || page.items.length > PAGE_SIZE
        || !Number.isSafeInteger(page.totalCount) || page.totalCount < 0
        || typeof page.coverageNote !== "string" || !page.coverageNote.trim()
        || typeof page.dateSemantics !== "string" || !page.dateSemantics.trim()) throw incomplete();
      const current = JSON.stringify([page.source, page.totalCount, page.coverageNote, page.dateSemantics]);
      if (metadata && fingerprint !== current) throw new Error("The split announcements changed while loading. Please try again.");
      if (!metadata) { const { items: ignored, ...summary } = page; metadata = summary; fingerprint = current; }
      for (const item of page.items) {
        const dateKey = `${item?.symbolId}/${item?.effectiveDate}`;
        if (!validItem(item, from, to) || seen.has(item.id) || seenDates.has(dateKey)) throw incomplete();
        seen.add(item.id); seenDates.add(dateKey); items.push(item);
      }
      if (items.length > page.totalCount) throw incomplete();
      onProgress({ loaded: items.length, total: page.totalCount });
      if (page.nextOffset === null) {
        if (items.length !== page.totalCount) throw incomplete();
        return { items, metadata };
      }
      if (!Number.isSafeInteger(page.nextOffset) || page.nextOffset !== offset + page.items.length
        || page.items.length === 0 || page.nextOffset >= page.totalCount) throw incomplete();
      offset = page.nextOffset;
    }
    throw new Error("There are too many recent split announcements to load. Please try again later.");
  }
}

function validItem(item, from, to) {
  if (!item || !validId(item.id) || !validId(item.symbolId) || typeof item.symbol !== "string" || !item.symbol.trim()
    || typeof item.companyName !== "string" || !item.companyName.trim() || item.status !== "Announced"
    || ![item.numerator, item.denominator, item.splitFactor].every((value) => typeof value === "number" && Number.isFinite(value) && value > 0)
    || typeof item.ratioNote !== "string" || !item.ratioNote.trim() || !validProvenance(item)) return false;
  const factor = item.numerator / item.denominator;
  if (!Number.isFinite(factor) || Math.abs(item.splitFactor / factor - 1) > 1e-12
    || !(item.splitType === "Forward" && item.numerator > item.denominator
      || item.splitType === "Reverse" && item.numerator < item.denominator)) return false;
  try { validateDateRange(item.effectiveDate, item.effectiveDate); } catch { return false; }
  return item.effectiveDate >= from && item.effectiveDate <= to;
}
function validProvenance(item) {
  if (!publicUrl(item.sourceUrl) || !timestamp(item.sourceCapturedAtUtc)
    || typeof item.originalBodySha256 !== "string" || !/^[a-f\d]{64}$/i.test(item.originalBodySha256)) return false;
  if (item.source === "TipRanks") return item.verification === "ProviderReported" && item.sourceContentKind === "RawHttp"
    && item.dateBasis === "ProviderAnnouncedDate";
  return item.source === "Issuer" && new URL(item.sourceUrl).protocol === "https:" && item.verification === "PrimarySource" && typeof item.publisherName === "string" && item.publisherName.trim()
    && ["RawHttp", "WebToolParsedExtract", "BrowserDomExtract"].includes(item.sourceContentKind)
    && ["ExDate", "SplitAdjustedTradingDate"].includes(item.dateBasis)
    && (item.publishedAtUtc == null || timestamp(item.publishedAtUtc) && Date.parse(item.publishedAtUtc) <= Date.parse(item.sourceCapturedAtUtc));
}
function validId(value) { return typeof value === "string" && GUID.test(value) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(value); }
function timestamp(value) { return typeof value === "string" && Number.isFinite(Date.parse(value)); }
function publicUrl(value) {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password && url.hostname.includes(".")
      && !["localhost", "[::1]"].includes(url.hostname) && !url.hostname.endsWith(".local")
      && !url.hostname.endsWith(".internal") && !url.hostname.endsWith(".localhost")
      && !url.hostname.includes(":") && !/^\d+(?:\.\d+){3}$/.test(url.hostname);
  } catch { return false; }
}
function incomplete() { return new Error("The recent split response was incomplete. Please try again."); }
